import _ from 'lodash';
import { generateDefaultHeatmapState } from '@/shared/lib/heatmap';
import { calculateReviewedStatus } from '@/widgets/beats/ui/reviewBeatsHelper';

/**
 * Builds the RR ratio axis category labels for the heat map. Steps from zero by the range increment producing dash-joined band labels, and renders the final band as a greater-or-equal label at the maximum.
 *
 * @param maxNumber - Upper bound of the axis, defaulting to four.
 * @param range - Width of each band, defaulting to one tenth.
 * @returns The ordered axis category labels.
 */
export const generateAxis = (maxNumber = 4, range = 0.1) => {
  let value = 0;
  const yAxis = [];
  let preValue = 0;
  const max = maxNumber + range;

  while (value < max) {
    value += range;
    if (value.toFixed(1) === max.toFixed(1)) {
      yAxis.push(`>= ${maxNumber.toFixed(1)}`);
    } else {
      yAxis.push(`${preValue.toFixed(preValue === 0 ? 0 : 1)}-${value.toFixed(1)}`);
    }

    preValue = value;
  }
  return yAxis;
};

export const CHART_INFO = {
  HEIGHT: 330,
  PLOT_HEIGHT: 291,
  COLOR_BREAKS: ['#DDD6FF', '#C2F0FF', '#FFF5C2', '#FFBA25', '#EB6D17'],
  GRID_LINE_COLOR: '#F5F7FA',
  XAXIS: generateAxis(),
  YAXIS: generateAxis(),
};

export const STATUS_CHECKBOX = {
  UNCHECK: 'UNCHECK',
  CHECK: 'CHECK',
  INDETERMINATE: 'INDETERMINATE',
};

/**
 * Strips the selected styling from a heat map point. Clears the class name and point padding and removes the marker outline colour and width.
 *
 * @param point - Heat map point to restore.
 * @param isClone - When true the point is shallow copied before mutation.
 * @returns The point with its selection styling removed.
 */
export const formatUnselectedPoint = (point: any = {}, isClone = false) => {
  const newPoint = isClone ? _.clone(point) : point;
  newPoint.className = '';
  newPoint.pointPadding = undefined;
  if (!newPoint.marker) {
    newPoint.marker = {};
  }
  newPoint.marker.lineColor = undefined;
  newPoint.marker.lineWidth = undefined;
  return newPoint;
};

/**
 * Applies the selected styling to a heat map point. Adds the selected-newPoint class with extra point padding and enables a blue one pixel marker outline.
 *
 * @param point - Heat map point to style.
 * @param isClone - When true the point is shallow copied before mutation.
 * @returns The styled point.
 */
export const formatSelectedPoint = (point: any = {}, isClone = false) => {
  const newPoint = isClone ? _.clone(point) : point;
  newPoint.className = 'selected-newPoint';
  newPoint.pointPadding = 1.2;
  if (!newPoint.marker) {
    newPoint.marker = {};
  }
  newPoint.marker.enabled = true;
  newPoint.marker.lineColor = '#0B68E5';
  newPoint.marker.lineWidth = 1;
  return newPoint;
};

/**
 * Blanks a heat map point so a fully reviewed cell renders as empty white space. Clears the class name and point padding, sets isEmpty with isReviewed false, paints it white and disables the hover marker state, mutating the point in place unless a clone is requested.
 *
 * @param point - Heat map point to blank.
 * @param isClone - When true the point is shallow copied before mutation.
 * @returns The blanked point.
 */
export const formatEmptyPoint = (point: any = {}, isClone = false) => {
  const newPoint = isClone ? _.clone(point) : point;
  newPoint.className = undefined;
  newPoint.pointPadding = undefined;
  newPoint.isReviewed = false;
  newPoint.isEmpty = true;
  const marker = {
    states: {
      hover: {
        enabled: false,
      },
    },
  };
  // newPoint.value = null;
  newPoint.color = '#ffffff';
  if (!newPoint.marker) {
    newPoint.marker = marker;
  } else {
    _.assign(newPoint.marker, marker);
  }

  return newPoint;
};

/**
 * Recolours a heat map point to reflect whether its beats are reviewed. Sets isReviewed, clears isEmpty, and paints reviewed points green while dropping the explicit colour on unreviewed ones so the colour axis takes over.
 *
 * @param point - Heat map point to recolour.
 * @param isReviewed - Whether the cell counts as fully reviewed.
 * @param isClone - When true the point is shallow copied before mutation.
 * @returns The recoloured point.
 */
export const formatReviewedPoint = (point: any = {}, isReviewed = false, isClone = false) => {
  const newPoint = isClone ? _.clone(point) : point;
  newPoint.isReviewed = isReviewed;
  newPoint.isEmpty = false;
  if (isReviewed) {
    newPoint.color = '#00B268';
  } else {
    newPoint.color = undefined;
  }
  return newPoint;
};

/**
 * Renders a beat count with thousands separators for the heat map tooltip. Stringifies the value and inserts commas every three digits using a lookahead regular expression.
 *
 * @param x - Value to format.
 * @returns The comma separated string, or undefined when the input is nullish.
 */
export const numberWithCommas = (x: any) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Abbreviates a beat count for the heat map colour axis labels. Returns an empty string for non-numbers and otherwise scales by thousand, million, billion or trillion, rounding to one decimal unless the value divides evenly.
 *
 * @param num - Value to abbreviate.
 * @returns The abbreviated number as a string, or an empty string when the input is not numeric.
 */
export const formatNumber = (num: any) => {
  if (typeof num !== 'number') {
    return '';
  }
  if (Math.abs(num) < 1e3) {
    return num.toString();
  }
  if (Math.abs(num) < 1e6) {
    return `${_.round(num / 1e3, num % 1e3 === 0 ? 0 : 1)}K`;
  }
  if (Math.abs(num) < 1e9) {
    return `${_.round(num / 1e6, num % 1e6 === 0 ? 0 : 1)}M`;
  }
  if (Math.abs(num) < 1e12) {
    return `${_.round(num / 1e9, num % 1e9 === 0 ? 0 : 1)}B`;
  }
  return `${_.round(num / 1e12, num % 1e12 === 0 ? 0 : 1)}T`;
};

const checkIsEmptyPoint = ({
  dataCell,
  isHideReviewedCell,
  maximumValueBeat,
  isInList,
  point,
}: {
  dataCell: any;
  isHideReviewedCell: any;
  maximumValueBeat: any;
  isInList?: any;
  point: any;
}) => {
  if (!dataCell?.count) {
    return true;
  }

  if ((isInList || dataCell?.isReviewed) && isHideReviewedCell) {
    return true;
  }
  if (point.isReviewed && isHideReviewedCell) {
    return true;
  }

  if (maximumValueBeat && dataCell.count > maximumValueBeat) {
    return true;
  }
  return false;
};

export const dataChartHeatMap = generateDefaultHeatmapState([]);

export const formatDataChart = ({
  currentDataHeatmapChart,
  isHideReviewedCell = false,
  maximumValueBeat = 0,
  listSelectedCells = [] as any[],
  type,
  resetBulkReviewedBeats,
  beatReviewChanges,
  bulkReviewedBeats,
}: {
  currentDataHeatmapChart: any[];
  isHideReviewedCell?: boolean;
  maximumValueBeat?: number;
  listSelectedCells?: any[];
  type: any;
  resetBulkReviewedBeats: any;
  beatReviewChanges: any;
  bulkReviewedBeats: any;
}) => {
  const dataChart: any[] = [];
  let maxValue = 0;

  const reversedObject: any = {};
  _.forEach(currentDataHeatmapChart, (item, index) => {
    const key = `${item.col}-${item.row}`;
    reversedObject[key] = index;
    if (item.count > maxValue) {
      maxValue = item.count;
    }
  });
  let index = -1;
  const yAxisLength = CHART_INFO.YAXIS.length;
  const xAxisLength = CHART_INFO.XAXIS.length;
  // y: row, x: col
  for (let y = 0; y < yAxisLength; y += 1) {
    for (let x = 0; x < xAxisLength; x += 1) {
      index += 1;
      const key = `${x}-${y}`;
      const point: any = {
        id: key,
        x,
        y,
        index,
        value: null,
        isReviewed: false,
        // reviewedCount: null,
        originalReviewedCount: 0,
        reviewedCount: 0,
        templates: [],
      };

      if (reversedObject?.[key] || reversedObject?.[key] === 0) {
        const dataCell = currentDataHeatmapChart[reversedObject[key]];

        point.value = dataCell?.count ? dataCell.count : null;
        // point.reviewedCount = !dataCell?.count ? null : dataCell.count - 10 > 0 ? dataCell.count - 10 : 0;
        point.id = dataCell?.id;
        point.templates = dataCell?.templates || [];
        point.originalReviewedCount = dataCell?.reviewed || 0;
        point.reviewedCount = dataCell?.reviewed || 0;
        const filterBeatChanges = _.filter(beatReviewChanges, (o) => o.id === dataCell.id);
        const { isReviewed } = calculateReviewedStatus({
          resetBulkReviewedBeats,
          item: point,
          filterBeatChanges,
          bulkReviewedBeats,
        });
        point.isReviewed = isReviewed;
        const isFormatReviewed = isReviewed && !isHideReviewedCell;
        const isFormatSelected = _.some(listSelectedCells, { id: dataCell.id });
        if (isFormatSelected) {
          formatSelectedPoint(point);
        }

        if (isFormatReviewed) {
          formatReviewedPoint(point, true);
        }

        if (!isFormatReviewed && !isFormatSelected) {
          const isFormatEmptyPoint = checkIsEmptyPoint({
            dataCell,
            isHideReviewedCell,
            maximumValueBeat,
            point,
          });
          if (isFormatEmptyPoint) {
            formatEmptyPoint(point);
          }
        }
      } else {
        formatEmptyPoint(point);
      }

      dataChart.push(point);
    }
  }
  (dataChartHeatMap as any)[type] = dataChart;
  return {
    dataChart,
    maxValue,
  };
};
